import { _GettersTree, defineStore } from 'pinia';
import { LessonPlan } from '@/models/LessonPlan.model';

import { useUsersStore } from "@/stores/Users.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { LessonPlanFilterDef } from "@/stores/filters/LessonPlanFilterDef";
import { AbstractBaseStore, AbstractStoreState, Method } from "@/stores/AbstractBase.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import { useLessonsStore } from "@/stores/Lessons.store.ts";
import { api } from "@/utils/Api.util.ts";
import { PaginationState } from "@/models/PaginationState.model";
import { OrderParameter } from "@/models/OrderParameter.model";

interface LessonPlansStoreState extends AbstractStoreState<LessonPlan, LessonPlanFilterDef> {
	latestNewPlan: LessonPlan | null,
	loadingMore: boolean,
	filter: LessonPlanFilterDef,
}

const BaseStore = AbstractBaseStore<LessonPlan, LessonPlansStoreState, LessonPlanFilterDef>();

export const useLessonPlansStore = defineStore('lessonPlans', {

	state: (): LessonPlansStoreState => {

		const defaultFilter = new LessonPlanFilterDef();
		const initialFilter = new LessonPlanFilterDef();
		const order = useDefaultOrderStore().lessonPlansOrder;

		return BaseStore.state({

			latestNewPlan: null,

			order,
			initialFilter,
			defaultFilter,
			loadingMore: false,

		});
	},

	getters: {

		...BaseStore.getters,

		apiPath() {
			return 'users/' + useUsersStore().getMe().getId() + '/lesson-plans';
		},
		apiClient(state): any {
			// Return the api client that should be used to make requests.
			// By default, this is the orgApi client.
			return api;
		},

		/**
		 * Get the default mask that should be included in every request.
		 * @param state
		 * @returns
		 */
		defaultMask(state): string[] {
			return [
				'*',
				'tags.*',
				'attachments.*',
				'supplies.*',
				'owner.id',
				'originalCreator.id',
				'originalCreator.name',
				'originalCreator.authorType',
				'originalCreator.publicName',
				'creator.id',
				'creator.name',
				'creator.authorType',
				'creator.publicName',
				'goals.*',
				'goals.tags.*',
				'collections.id',
			];
		},

		/**
		 * @returns number
		 */
		defaultRecordsPerPage(): number {
			return 25;
		},

	} as _GettersTree<LessonPlansStoreState>,

	actions: {

		...BaseStore.actions,

		async new(
			inLibrary: boolean = false
		) {

			const plan = LessonPlan.new();
			this.latestNewPlan = plan;

			plan.isInMyLibrary = inLibrary;

			return plan;

		},

		mapToItem(data: any): LessonPlan {
			return LessonPlan.createFromApiData(data);
		},

		async save(item: LessonPlan) {

			// delete empty supplies
			// item.supplies = item.supplies.filter((supply) => supply !== "");

			await this.superSave(item);
			// Reload the collections as the plan may have been added to collections
			useLessonPlanCollectionsStore().load(true);

			// Agenda lessons that use this plan are updated in afterAlteredUpdated().

		},

		/**
		 * Saving a lesson plan creates a new version. The server moves the lesson items of lessons that
		 * have not ended yet along to that version and lists them in the response; lesson items in past
		 * lessons keep the version they were saved with. Only the listed lesson items get the saved plan.
		 *
		 * We can't simply reload the lessons store, as we might be resetting the lesson that is currently
		 * loaded. This applies to the scenario where you edit a library-saved lesson plan from within an
		 * agenda lesson: modifications to the lesson plan are immediately sent to the server while the
		 * lesson editor is still open.
		 * @param updated
		 * @param savedModels
		 */
		async afterAlteredUpdated(updated: { id: string, type: string }[], savedModels: LessonPlan[]) {

			const lessonItemIds = updated
				.filter((entry) => entry.type === 'lessonitem')
				.map((entry) => entry.id);

			this.updateLessonPlansInLessonStore(savedModels, lessonItemIds);

		},

		/**
		 * Add an existing lesson plan to your library without making a duplicate
		 * @param item
		 */
		async addToLibrary(item: LessonPlan) {

			const response = await this.apiClient.post(
				this.apiUrl(Method.CREATE),
				{
					originalId: item.id,
					version: item.version,
				},
				{
					params: {
						mask : this.defaultMask.join(','),
					}
				}
			);

			item.hydrateWithApiData(response.data.data);

			useLessonPlanCollectionsStore().load(true);

			// The plan usually comes from outside the library list (e.g. the discover page), so it
			// is not one of our items. Refresh an already loaded list in the background; otherwise
			// loadOnce() keeps serving the list from before the plan was added.
			if (this.loadedOnce) {
				this.page = null;
				this.load(true);
			}

			this.updateLessonPlansInLessonStore([ item ]);

		},

		/**
		 * Go through all agenda lessons and update the lesson plan if it matches the lesson plan id.
		 * @param lessonPlans
		 * @param lessonItemIds When set, only these lesson items are updated (the ones the server moved
		 * along to the new version of the plan). When not set, the plan did not get a new version and all
		 * lesson items that use the same version are updated.
		 */
		updateLessonPlansInLessonStore(lessonPlans: LessonPlan[], lessonItemIds?: string[]) {
			const lessons = useLessonsStore().events;

			lessons.forEach((lesson) => {
				if (!lesson.lessonItems) {
					return;
				}

				lesson.lessonItems.forEach((lessonItem) => {
					if (!lessonItem.lessonPlan) {
						return;
					}

					if (lessonItemIds && (!lessonItem.id || !lessonItemIds.includes(lessonItem.id))) {
						// It keeps its version, which may no longer be the latest one
						const updatedPlan = lessonPlans.find((plan) => plan.id === lessonItem.lessonPlan?.id);
						if (updatedPlan?.latestVersion !== undefined && (lessonItem.lessonPlan.latestVersion ?? -1) < updatedPlan.latestVersion) {
							lessonItem.lessonPlan.latestVersion = updatedPlan.latestVersion;
						}
						return;
					}

					lessonPlans.some((updatedPlan) => {
						if (lessonItem.lessonPlan?.id !== updatedPlan.id) {
							return false;
						}

						// Lesson items are pinned to a specific plan version, so without a list from the
						// server a lesson item that uses another version must keep it.
						if (!lessonItemIds && lessonItem.lessonPlan?.version !== updatedPlan.version) {
							return false;
						}

						lessonItem.lessonPlan = updatedPlan;
						return true;
					});
				});
			});
		},

		// When a lesson plan is removed from my library, the agenda lessons that use it keep the very
		// same plan; they only need to know it's no longer in my library or my collections. This is the
		// same for the owner: the API only removes the plan from the caller's own library.
		async afterDelete(item: LessonPlan) {
			const lessons = useLessonsStore().events;
			lessons.forEach((lesson) => {
				lesson.lessonItems?.forEach((lessonItem) => {
					if (lessonItem.lessonPlan && lessonItem.lessonPlan.id === item.id) {
						lessonItem.lessonPlan.isInMyLibrary = false;
						lessonItem.lessonPlan.collectionIds = [];
					}
				});
			});
		},

		updateStoreItemFromNewItem(existingModel: LessonPlan, newData: LessonPlan) {
			existingModel.setFromModel(newData);
		},

		/**
		 * Duplicate a lesson plan to the current user's library.
		 * Uses org-scoped endpoint: org/{orgId}/users/{userId}/lesson-plans/{planId}/duplicate
		 */
		async duplicate(item: LessonPlan) {
			const me = useUsersStore().getMe();
			const org = useOrganisationsStore().getCurrentOrganisation();

			try {
				const response = await api.post(
					`${this.apiPath}/${item.id}/duplicate`,
					{
						targetUser: { id: me.getId() },
						targetOrganisation: { id: org.id },
					},
					{
						params: {
							mask: this.defaultMask.join(','),
						},
					}
				);

				const newPlan = LessonPlan.createFromApiData(response.data.data);
				newPlan.isInMyLibrary = true;
				this.addStoreItem(newPlan);

				return newPlan;
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		/**
		 * Fetch lesson plans belonging to a specific collection with pagination.
		 * Uses the standard lesson-plans endpoint with a collections filter.
		 */
		async loadByCollection(collectionId: string, page: PaginationState | null = null, order: OrderParameter | null = null): Promise<{ items: LessonPlan[], page: PaginationState | null }> {
			const response = await this.apiClient.get(
				'lesson-plans', // we need to override the url so we don't use the profile specific one.
				{
					params: {
						mask: this.defaultMask.join(','),
						collections: collectionId,
						page: page ? page.currentPage : 1,
						order: order ? order.getApiProperty() : this.order.getApiProperty(),
						recordsPerPage: 25,
					},
				});
			const items = response.data?.data ?? response.data ?? [];
			const paginationState = response.data?.meta ? PaginationState.mapFromServer(response.data.meta) : null;
			return {
				items: (Array.isArray(items) ? items : []).map((d: any) => this.mapToItem(d)),
				page: paginationState,
			};
		},

		applyOrder(order: OrderParameter) {
			useDefaultOrderStore().setDefaultLessonPlansOrder(order);
			this.superApplyOrder(order);
		}

	},
});
